import React, { createContext, useContext, useState, useEffect } from 'react';
import { taskDetailsService } from '../features/task/services/taskDetailsService';
import { twentyFourHrFormat, convertMinutesToHoursAndMinutesShort, parseAndConvertToLocal } from '../shared/utils/dateFormatter';
import { firebaseNotificationService } from '../Firebase';

const MaintenanceContext = createContext();

export const useMaintenanceContext = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceContext must be used within a MaintenanceProvider');
  }
  return context;
};

const getPriorityText = (priority) => {
  const priorityMap = { 0: 'Low', 1: 'Medium', 2: 'Normal', 3: 'High', 4: 'Urgent' };
  return priorityMap[priority] || 'N/A';
};

const mapMaintenanceData = (apiResponse) => {
  // Handle different response structures
  const data = apiResponse.data || apiResponse;
  console.log('Data being mapped:', data);
  
  return {
    id: data.id,
    reqId: data.org_task_code,
    customerId: data.customer_id,
    companyName: data.company_name,
    customerName: `${data.company_name}`,
    phoneNumber: data.phone_number,
    email: data.email,
    address: {
      address1: data.address1,
      address2: data.address2,
      fullAddress: data.Issues?.client_address ?
        [data.Issues.client_address.address1, data.Issues.client_address.city, data.Issues.client_address.state, data.Issues.client_address.zip_code]
          .filter(Boolean).join(', ') || 'N/A' : 'N/A',
      coordinates: { lat: data.lat, lng: data.lng }
    },
    equipment: {
      id: data.client_equipment_id,
      equipmentName: data.product_name,
      product: data.item_category,
      make: data.make_name,
      model: data.model_name,
    },
    warrantyType: data.contract_type,
    technician: {
      id: data.employee_id,
      name: data.employee_name
    },
    schedule: {
      priority: getPriorityText(data.priority),
      priorityRaw: data.priority,
      scheduleType: data.schedule_type,
      scheduledDate: data.scheduled_date || null,
      scheduledTime: data.scheduled_date ? twentyFourHrFormat(parseAndConvertToLocal(data.scheduled_date), true) : 'N/A',
      endDate: data.scheduled_end_date ? new Date(data.scheduled_end_date).toLocaleDateString() : 'N/A',
      endTime: data.scheduled_end_date ? new Date(data.scheduled_end_date).toLocaleTimeString() : 'N/A',
      travelTime: convertMinutesToHoursAndMinutesShort(data.travel_time),
      taskDuration: convertMinutesToHoursAndMinutesShort(data.task_duration),
      recurrence: {
        type: data.recurrence_type,
        frequency: data.repeat_frequency,
        endDate: data.recurrence_end_date,
        occurrences: data.recurrence_occurrences
      }
    },
    scheduledDate: data.scheduled_date,
    issues: {
      details: data.Issues?.issue_details || [],
      pictures: data.Issues?.issue_pics || [],
      videos: data.Issues?.issue_videos || [],
      additionalNotes: data.Issues?.additional_notes || ''
    },
    status: data.status,
    reportedBy: data.Reported_details,
    reportedDate: data.Reported_date || null,
    inspectionComments: data.inspection_comments ?
      (typeof data.inspection_comments === 'string' ?
        JSON.parse(data.inspection_comments) : data.inspection_comments) : [],
    workDoneComments: data.work_done_comments || [],
    inspectionFiles: data.inspection_files || [],
    workReportFiles: data.work_report_files || [],
    estimation: data.estimation_details,
    workReport: data.work_Report_details ? JSON.parse(data.work_Report_details) : null,
    clientRepName: data.client_rep_name,
    estimationSignature: data.estimation_signature || [],
    serviceReportSignature: data.service_report_signature || [],
    observations: data.observation || [],
    isEquipmentVerified: data.is_client_equipment_verified === 1,
    recurrenceRules: data.r_rules ? JSON.parse(data.r_rules) : null,
    contractType: data.contract_type
  };
};

export const MaintenanceProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState(null);

  const fetchMaintenanceDetails = async (id, showLoader = true) => {
    if (!id) {
      console.error('No maintenance request ID provided');
      return null;
    }

    try {
      if (showLoader) {
        setIsLoading(true);
      }
      const response = await taskDetailsService.getMaintenanceRequestDetails(id);
      console.log('Raw API response:', response);

      if (response.success) {
        const mappedData = mapMaintenanceData(response);
        console.log('Mapped data:', mappedData);
        setMaintenanceData(mappedData);
        return mappedData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching maintenance details:', error);
      return null;
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  // Register the fetchMaintenanceDetails function with Firebase service
  useEffect(() => {
    firebaseNotificationService.setFetchMaintenanceDetailsCallback(fetchMaintenanceDetails);
    
    return () => {
      firebaseNotificationService.setFetchMaintenanceDetailsCallback(null);
    };
  }, []);

  const value = {
    isLoading,
    maintenanceData,
    fetchMaintenanceDetails,
    setMaintenanceData
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};